import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, StreamVideo, embedCode, embedUrl } from "../lib/api";
import { CopyField } from "../components/CopyField";
import { DeleteVideoModal } from "../components/DeleteVideoModal";

export function Library() {
  const [videos, setVideos] = useState<StreamVideo[] | null>(null);
  const [customerDomain, setCustomerDomain] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [toDelete, setToDelete] = useState<StreamVideo | null>(null);

  async function load() {
    setError(null);
    try {
      const res = await api.listVideos(search);
      setVideos(res.videos);
      setCustomerDomain(res.customerDomain ?? "");
    } catch (e) {
      setError((e as Error).message);
    }
  }

  useEffect(() => {
    load();
    const t = setInterval(() => {
      if (videos?.some((v) => !v.readyToStream)) load();
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  async function handleDelete() {
    if (!toDelete) return;
    await api.deleteVideo(toDelete.uid);
    setToDelete(null);
    await load();
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">Library</h1>
        <div className="flex items-center gap-2">
          <input
            type="search"
            placeholder="Search title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input w-64"
          />
          <Link to="/upload" className="btn-primary">
            Upload video
          </Link>
        </div>
      </div>

      {error && (
        <div className="card p-4 border-red-300 bg-red-50 text-red-800 text-sm">
          {error}
          {error.includes("CF_") && (
            <p className="mt-2 text-xs">
              Check that env vars <code>CF_ACCOUNT_ID</code>, <code>CF_STREAM_TOKEN</code>, and{" "}
              <code>CF_STREAM_CUSTOMER_DOMAIN</code> are set on the Worker.
            </p>
          )}
        </div>
      )}

      {videos === null && !error && (
        <div className="text-neutral-500 text-sm">Loading…</div>
      )}

      {videos && videos.length === 0 && (
        <div className="card p-8 text-center text-neutral-500">
          No videos yet. <Link to="/upload" className="underline">Upload your first one</Link>.
        </div>
      )}

      <div className="space-y-3">
        {videos?.map((v) => (
          <VideoRow
            key={v.uid}
            v={v}
            customerDomain={customerDomain}
            onDelete={() => setToDelete(v)}
          />
        ))}
      </div>

      <DeleteVideoModal
        open={!!toDelete}
        videoTitle={toDelete?.meta.name || toDelete?.uid || ""}
        onCancel={() => setToDelete(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}

function VideoRow({
  v,
  customerDomain,
  onDelete,
}: {
  v: StreamVideo;
  customerDomain: string;
  onDelete: () => void;
}) {
  const [embedOpen, setEmbedOpen] = useState(false);
  const title = v.meta.name || v.uid;
  const url = customerDomain ? embedUrl(customerDomain, v.uid) : "";
  const code = customerDomain ? embedCode(customerDomain, v.uid, title) : "";
  const canShowEmbed = v.readyToStream && customerDomain;

  return (
    <div className="card overflow-hidden">
      <div className="flex gap-4 p-4 items-center">
        <Link
          to={`/videos/${v.uid}`}
          className="shrink-0 w-32 aspect-video bg-neutral-900 rounded overflow-hidden"
        >
          {v.thumbnail ? (
            <img
              src={`${v.thumbnail}?v=${encodeURIComponent(v.modified)}`}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-neutral-400 text-xs">
              processing
            </div>
          )}
        </Link>

        <div className="min-w-0 flex-1">
          <Link to={`/videos/${v.uid}`} className="font-medium truncate block hover:underline">
            {title}
          </Link>
          <div className="text-xs text-neutral-500 mt-0.5">
            {formatDuration(v.duration)} · {formatBytes(v.size)} ·{" "}
            {new Date(v.created).toLocaleDateString()}
          </div>
          <StatusBadge v={v} />
        </div>

        <div className="shrink-0 flex items-center gap-2">
          {canShowEmbed && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setEmbedOpen((o) => !o)}
              aria-expanded={embedOpen}
            >
              {embedOpen ? "Hide embed" : "Show embed"}
              <Chevron open={embedOpen} />
            </button>
          )}
          <button
            type="button"
            className="text-neutral-400 hover:text-red-600 p-2 rounded-md hover:bg-red-50 transition-colors"
            aria-label={`Delete ${title}`}
            title="Delete video"
            onClick={onDelete}
          >
            <TrashIcon />
          </button>
        </div>
      </div>

      {canShowEmbed && embedOpen && (
        <div className="border-t border-neutral-200 p-4 space-y-3 bg-neutral-50">
          <CopyField label="Embed URL" value={url} />
          <CopyField label="Embed code" value={code} multiline />
        </div>
      )}
    </div>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      className={`transition-transform ${open ? "rotate-180" : ""}`}
      aria-hidden="true"
    >
      <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M2 4h12M6 4V2.5A.5.5 0 0 1 6.5 2h3a.5.5 0 0 1 .5.5V4m1 0v9a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4h8zM6.5 7v4M9.5 7v4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function StatusBadge({ v }: { v: StreamVideo }) {
  if (v.readyToStream) {
    return (
      <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">
        Ready
      </span>
    );
  }
  if (v.status.state === "error") {
    return (
      <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800">
        Error: {v.status.errorReasonText || "unknown"}
      </span>
    );
  }
  return (
    <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
      {v.status.state}
      {v.status.pctComplete ? ` · ${v.status.pctComplete}%` : ""}
    </span>
  );
}

function formatBytes(n: number) {
  if (!n) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatDuration(seconds: number) {
  if (!seconds) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
}
